/**
 * router/index.ts
 *
 * Automatic routes for `./src/pages/*.vue`
 */

// Composables
import { createRouter, createWebHistory } from 'vue-router/auto'
import { setupLayouts } from 'virtual:generated-layouts'
import { routes } from 'vue-router/auto-routes'
import { getCurrentAccount } from '@/plugins/msal'
import { useUserRoles } from '@/composables/useUserRoles'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: setupLayouts(routes),
})

// Workaround for https://github.com/vitejs/vite/issues/11804
router.onError((err, to) => {
  if (err?.message?.includes?.('Failed to fetch dynamically imported module')) {
    if (!localStorage.getItem('vuetify:dynamic-reload')) {
      console.log('Reloading page to fix dynamic import error')
      localStorage.setItem('vuetify:dynamic-reload', 'true')
      location.assign(to.fullPath)
    } else {
      console.error('Dynamic import error, reloading page did not fix it', err)
    }
  } else {
    console.error(err)
  }
})

router.beforeEach(async (to, from, next) => {
  const { resolveAndSetRoles, clearRoles } = useUserRoles()

  // Altijd rollen laden als user ingelogd is
  const account = await getCurrentAccount()

  if (account) {
    try {
      // Eén Graph-call voor alle groepslidmaatschappen tegelijk
      const { isAdmin, isTimesheetAdmin, isProjectIntake, isCompass } = await resolveAndSetRoles()
      const hasAdminAccess = isAdmin || isTimesheetAdmin

      if (to.meta.requiresFullAdmin) {
        // Full admin only (not timesheet admin)
        if (isAdmin) {
          next()
        } else {
          console.warn('User does not have full admin access')
          next('/')
        }
      } else if (to.meta.requiresAdmin) {
        if (hasAdminAccess) {
          next()
        } else {
          console.warn('User does not have admin access')
          next('/')
        }
      } else if (to.meta.requiresProjectIntake) {
        const hasProjectIntakeAccess = isProjectIntake || isAdmin

        if (hasProjectIntakeAccess) {
          next()
        } else {
          console.warn('User does not have project intake access')
          next('/')
        }
      } else if (to.meta.requiresCompass) {
        // Alleen de Compass-groep, ook geen admin-uitzondering (gelijk aan de API)
        if (isCompass) {
          next()
        } else {
          console.warn('User does not have Compass access')
          next('/')
        }
      } else {
        next()
      }
    } catch (error) {
      console.error('Error checking user roles:', error)
      clearRoles()
      next('/')
    }
  } else {
    if (
      to.meta.requiresAdmin ||
      to.meta.requiresFullAdmin ||
      to.meta.requiresProjectIntake ||
      to.meta.requiresCompass
    ) {
      next('/')
    } else {
      next()
    }
  }
})

router.isReady().then(() => {
  localStorage.removeItem('vuetify:dynamic-reload')
})

export default router
